import * as THREE from 'three';
import type { Quality, ShipKind } from '../game/types';
import { Assets } from './models';

/** Uses the game's renderer and exact ship templates: one WebGL context, shared assets. */
export class ShipPreview {
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(38, 1, .1, 300);
  private readonly light = new THREE.DirectionalLight(0xffedcc, 3.1);
  private readonly models = new Map<ShipKind, THREE.Group>();
  private readonly abort = new AbortController();
  private readonly pointers = new Map<number, { x: number; y: number }>();
  private kind: ShipKind = 'frigate';
  private yaw = -.24;
  private magnification = 1;
  private framingKey = '';

  constructor(private readonly renderer: THREE.WebGLRenderer, assets: Assets, templates: Map<string, THREE.Group>, private readonly surface: HTMLElement) {
    this.scene.add(new THREE.HemisphereLight(0xf0f6ef, 0x7b9290, 2.25));
    this.light.position.set(-24, 42, 28); this.light.castShadow = true;
    this.light.shadow.mapSize.set(1024, 1024); this.light.shadow.camera.left = this.light.shadow.camera.bottom = -25;
    this.light.shadow.camera.right = this.light.shadow.camera.top = 25; this.light.shadow.camera.near = 1; this.light.shadow.camera.far = 100;
    this.light.shadow.bias = -.001; this.light.shadow.normalBias = .035; this.scene.add(this.light, this.light.target);
    for (const kind of ['sloop', 'frigate', 'galleon'] as ShipKind[]) {
      const model = templates.get(`player-${kind}`)!.clone(true); model.visible = kind === this.kind;
      this.models.set(kind, model); this.scene.add(model);
    }
    const shadow = new THREE.Mesh(assets.geometry(new THREE.PlaneGeometry(100, 100)), assets.material(new THREE.ShadowMaterial({ opacity: .18, depthWrite: false })));
    shadow.rotation.x = -Math.PI / 2; shadow.position.y = -.29; shadow.receiveShadow = true; this.scene.add(shadow);
    const signal = this.abort.signal;
    surface.addEventListener('pointerdown', event => {
      if (event.button !== 0) return;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY }); surface.setPointerCapture(event.pointerId);
      surface.classList.add('dragging'); event.preventDefault();
    }, { signal });
    surface.addEventListener('pointermove', event => {
      const old = this.pointers.get(event.pointerId); if (!old) return;
      const other = [...this.pointers.entries()].find(([id]) => id !== event.pointerId)?.[1];
      if (other) {
        const before = Math.hypot(old.x - other.x, old.y - other.y), after = Math.hypot(event.clientX - other.x, event.clientY - other.y);
        if (before > 8 && after > 8) this.magnification = THREE.MathUtils.clamp(this.magnification * after / before, .75, 1.5);
      } else this.rotate((event.clientX - old.x) * .012);
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY }); event.preventDefault();
    }, { signal });
    const release = (event: PointerEvent) => { this.pointers.delete(event.pointerId); if (!this.pointers.size) surface.classList.remove('dragging'); };
    for (const type of ['pointerup', 'pointercancel', 'lostpointercapture'] as const) surface.addEventListener(type, release, { signal });
    surface.addEventListener('wheel', event => { this.zoom(-Math.sign(event.deltaY)); event.preventDefault(); event.stopPropagation(); }, { passive: false, signal });
    surface.addEventListener('keydown', event => {
      if (event.key === 'ArrowLeft') this.rotate(-.18);
      else if (event.key === 'ArrowRight') this.rotate(.18);
      else if (event.key === '+' || event.key === '=') this.zoom(1);
      else if (event.key === '-') this.zoom(-1);
      else if (event.key === 'Home') this.reset();
      else return;
      event.preventDefault();
    }, { signal });
    this.select(this.kind);
  }

  select(kind: ShipKind): void {
    if (kind !== this.kind) this.reset();
    this.kind = kind; this.models.forEach((model, key) => { model.visible = key === kind; });
    this.surface.dataset.previewShip = kind;
  }
  rotate(delta: number): void { this.yaw += delta; }
  zoom(delta: number): void { this.magnification = THREE.MathUtils.clamp(this.magnification + delta * .1, .75, 1.5); }
  reset(): void { this.yaw = -.24; this.magnification = 1; this.pointers.clear(); this.surface.classList.remove('dragging'); }
  setQuality(quality: Quality): void { this.light.castShadow = quality === 'standard'; }

  private frameModel(aspect: number): void {
    const key = `${this.kind}:${this.yaw}:${this.magnification}:${aspect}`;
    if (key === this.framingKey) return;
    this.framingKey = key;
    const model = this.models.get(this.kind)!; model.rotation.y = this.yaw; model.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(model), center = box.getCenter(new THREE.Vector3());
    const direction = new THREE.Vector3(.7, .40, 1).normalize();
    // Fit actual mesh vertices; empty corners of a hull-sized box waste the preview space.
    this.camera.aspect = aspect; this.camera.position.copy(center).addScaledVector(direction, 80); this.camera.lookAt(center); this.camera.updateMatrixWorld(true);
    const inverse = this.camera.matrixWorldInverse, tan = Math.tan(THREE.MathUtils.degToRad(this.camera.fov / 2));
    let distance = 0;
    const p = new THREE.Vector3(), matrix = new THREE.Matrix4();
    model.traverse(child => {
      if (!(child instanceof THREE.Mesh)) return;
      matrix.multiplyMatrices(inverse, child.matrixWorld); const positions = child.geometry.getAttribute('position');
      for (let i = 0; i < positions.count; i++) {
        p.fromBufferAttribute(positions, i).applyMatrix4(matrix);
        distance = Math.max(distance, Math.abs(p.y) / tan + p.z + 80, Math.abs(p.x) / (tan * aspect) + p.z + 80);
      }
    });
    this.camera.position.copy(center).addScaledVector(direction, distance * 1.045 / this.magnification); this.camera.lookAt(center); this.camera.updateProjectionMatrix();
  }

  render(): void {
    const rect = this.surface.getBoundingClientRect(), canvas = this.renderer.domElement.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1 || rect.bottom <= canvas.top || rect.top >= canvas.bottom) return;
    const x = rect.left - canvas.left, y = canvas.bottom - rect.bottom;
    this.frameModel(rect.width / rect.height);
    this.renderer.setViewport(x, y, rect.width, rect.height);
    this.renderer.setScissor(Math.max(0, x), Math.max(0, y), Math.min(rect.right, canvas.right) - Math.max(rect.left, canvas.left), Math.min(rect.bottom, canvas.bottom) - Math.max(rect.top, canvas.top));
    this.renderer.setScissorTest(true); this.renderer.autoClear = false; this.renderer.clearDepth(); this.renderer.render(this.scene, this.camera);
    this.renderer.setScissorTest(false); this.renderer.setViewport(0, 0, canvas.width, canvas.height); this.renderer.autoClear = true;
  }

  thumbnails(): Record<ShipKind, string> {
    const target = new THREE.WebGLRenderTarget(320, 224), data = new Uint8Array(320 * 224 * 4);
    const result = {} as Record<ShipKind, string>, selected = this.kind;
    const clear = this.renderer.getClearColor(new THREE.Color()), alpha = this.renderer.getClearAlpha();
    for (const kind of this.models.keys()) {
      this.select(kind); this.frameModel(320 / 224);
      this.renderer.setRenderTarget(target); this.renderer.setClearColor(0x000000, 0); this.renderer.clear(); this.renderer.render(this.scene, this.camera);
      this.renderer.readRenderTargetPixels(target, 0, 0, 320, 224, data);
      const canvas = document.createElement('canvas'); canvas.width = 320; canvas.height = 224;
      const ctx = canvas.getContext('2d')!; const pixels = ctx.createImageData(320, 224);
      for (let row = 0; row < 224; row++) pixels.data.set(data.subarray(row * 320 * 4, (row + 1) * 320 * 4), (223 - row) * 320 * 4);
      ctx.putImageData(pixels, 0, 0); result[kind] = canvas.toDataURL('image/png');
    }
    this.renderer.setRenderTarget(null); this.renderer.setClearColor(clear, alpha); target.dispose(); this.select(selected); this.reset(); return result;
  }
  state(): { kind: ShipKind; yaw: number; zoom: number } { return { kind: this.kind, yaw: this.yaw, zoom: this.magnification }; }
  dispose(): void { this.abort.abort(); this.pointers.clear(); this.light.shadow.map?.dispose(); this.scene.clear(); }
}
